// The copy for every BUILD 2 error code and reason (C2-14): the server sends
// codes and structural reasons only (src/voice-errors.mjs), and each maps
// here to a key of the namespace whose surface shows it. A code more than one
// surface answers (a 402 from any metered route, `voice_unavailable` from the
// meeting voice pass or voice signatures) takes the asking surface's own copy.
// The reason arrives on the request error (CompanyRequestError.reason,
// SpeechHttpError.reason). test/voice-error-copy.mjs pins that no code,
// reason, lane failure code or shared code is missing. Written by WP2-0b; a
// new code is a CCR on both halves.
import type { ReadAloudCopyKey } from '@/i18n/ns/read-aloud';
import type { MeetingsVoiceCopyKey } from '@/i18n/ns/meetings-voice';
import type { VoiceCopyKey } from '@/i18n/ns/voice';
import type { VoiceConfirmCopyKey } from '@/i18n/ns/voice-confirm';
import type { VoiceSignatureCopyKey } from '@/i18n/ns/voice-signature';

export type VoiceCopyRef =
  | { ns: 'read-aloud'; key: ReadAloudCopyKey }
  | { ns: 'voice'; key: VoiceCopyKey }
  | { ns: 'voice-confirm'; key: VoiceConfirmCopyKey }
  | { ns: 'meetings-voice'; key: MeetingsVoiceCopyKey }
  | { ns: 'voice-signature'; key: VoiceSignatureCopyKey };

/** A code's copy, or its copy per structural `reason`. */
export type VoiceErrorCopy = VoiceCopyRef | { byReason: Record<string, VoiceCopyRef> };
/** The surface showing an error: the namespace its copy comes from. */
export type VoiceSurface = VoiceCopyRef['ns'];

const readAloud = (key: ReadAloudCopyKey): VoiceCopyRef => ({ ns: 'read-aloud', key });
const voice = (key: VoiceCopyKey): VoiceCopyRef => ({ ns: 'voice', key });
const confirm = (key: VoiceConfirmCopyKey): VoiceCopyRef => ({ ns: 'voice-confirm', key });
const meetings = (key: MeetingsVoiceCopyKey): VoiceCopyRef => ({ ns: 'meetings-voice', key });
const signature = (key: VoiceSignatureCopyKey): VoiceCopyRef => ({ ns: 'voice-signature', key });

const voicePassUnavailable = meetings('errorUnavailable');

export const VOICE_ERROR_COPY: Record<string, VoiceErrorCopy> = {
  // Read-aloud provider failures are shown with the dedicated TTS copy on
  // every surface that does not provide a more specific voice-pass message.
  tts_unavailable: voice('errorTtsUnavailable'),
  tts_language_unsupported: readAloud('noVoiceForLanguage'),
  tts_source_mismatch: readAloud('couldNotRead'),
  tts_budget_exhausted: readAloud('errorBudgetExhausted'),
  tts_sentence_not_ready: readAloud('loadingAudio'),
  voice_unavailable: {
    byReason: {
      disabled: voicePassUnavailable,
      key_missing: signature('unavailable'),
      models_missing: voicePassUnavailable,
      runtime_missing: voicePassUnavailable,
      model_integrity: voicePassUnavailable,
    },
  },
  voice_org_disabled: signature('orgOff'),
  voice_pass_not_current: meetings('errorPassNotCurrent'),
  voice_consent_required: signature('errorConsentRequired'),
  voice_consent_version_mismatch: signature('errorConsentChanged'),
  voice_not_subject: signature('errorNotSubject'),
  voice_purge_confirmation_required: signature('errorPurgeConfirmationRequired'),
  voice_enrolment_too_short: signature('tooShort'),
  voice_enrolment_multiple_speakers: signature('multipleSpeakers'),
  voice_enrolment_poor_audio: signature('poorAudio'),
  voice_enrolment_not_eligible: signature('errorNotEligible'),
  voice_enrolment_expired: signature('errorExpired'),
  voice_suggestion_not_found: meetings('errorSuggestionGone'),
  voice_suggestion_stale: meetings('errorSuggestionGone'),
  voiceprint_model_stale: signature('staleModel'),
  voice_mode_unavailable: {
    byReason: {
      not_configured: voice('errorNotConfigured'),
      policy_off: voice('errorPolicyOff'),
      no_allowlisted_engine: voice('errorNoEngine'),
      provider_unavailable: voice('errorProviderUnavailable'),
    },
  },
  // A meeting's voice pass failure code (`voice.failureCode`), never an HTTP answer.
  voice_job_timeout: meetings('voiceCheckFailed'),
  voice_engine_failed: meetings('voiceCheckFailed'),
  voice_decode_failed: meetings('voiceCheckFailed'),
  voice_decrypt_failed: meetings('voiceCheckFailed'),
  voice_model_integrity: meetings('voiceCheckFailed'),
};

/**
 * Codes more than one surface answers, in each surface's own words: main's
 * 402 `payment_required` (the company's usage credit is used up) from
 * read-aloud, the confirmation summary and interpreter and the voice-mode
 * mint, and `voice_unavailable` on the voice-signature surface, whatever its
 * reason.
 */
export const VOICE_SURFACE_COPY: Record<VoiceSurface, Record<string, VoiceCopyRef>> = {
  'read-aloud': { payment_required: readAloud('errorPaymentRequired') },
  voice: { payment_required: voice('errorPaymentRequired') },
  'voice-confirm': { payment_required: confirm('errorPaymentRequired') },
  'meetings-voice': {},
  'voice-signature': { voice_unavailable: signature('unavailable') },
};

/**
 * The copy a surface shows for a code and its reason; null for a code this
 * surface does not answer. An unknown reason takes the code's first copy.
 */
export function voiceErrorCopy(
  surface: VoiceSurface,
  code: string,
  reason?: string | null,
): VoiceCopyRef | null {
  const shared = VOICE_SURFACE_COPY[surface][code];
  if (shared) return shared;
  const copy = VOICE_ERROR_COPY[code];
  if (!copy) return null;
  if (!('byReason' in copy)) return copy;
  return (reason && copy.byReason[reason]) || Object.values(copy.byReason)[0];
}
